import { calculatePackagePricePPPW } from "@/utility/priceCalculation";

export default {
  leadType(state) {
    return state.personalDetails.leadType;
  },
  firstName(state) {
    return state.personalDetails.firstname;
  },
  lastName(state) {
    return state.personalDetails.lastname;
  },
  emailAddress(state) {
    return state.personalDetails.email;
  },
  telNumber(state) {
    return state.personalDetails.tel;
  },
  housemateCount(state) {
    return state.housemateCount;
  },
  postcode(state) {
    return state.houseInformation.postcode;
  },
  nonDiscountedPricePerPerson(state) {
    return calculatePackagePricePPPW(state, false);
  },
  nonDiscountedPricePerPersonPerMonth(state) {
    //Convert weekly package amount from pence back to pounds
    let pppwInPounds = calculatePackagePricePPPW(state, false) / 100;

    //Calculate the average price per month
    let pppmInPounds = pppwInPounds * 4.34524;

    // Return back in pence for consistency
    return pppmInPounds * 100;
  },
  pricePerPerson(state) {
    return calculatePackagePricePPPW(state, true);
  },
  pricePerPersonPerMonth(state) {
    //The monthly payment is calculated by dividing the weekly cost by 7 (days),
    //multiplying by the number of days in the year, and dividing by 12
    //(months). Part months are calculated for the number of days at the daily
    //rate.
    // The multiplier 4.34524 comes from: 365 days in a year ÷ 7 days per week ÷ 12 months
    // ≈ 4.34524 weeks in an average month.

    //Convert weekly package amount from pence back to pounds
    let pppwInPounds = calculatePackagePricePPPW(state, true) / 100;

    //Calculate the average price per month
    let pppmInPounds = pppwInPounds * 4.34524;

    // Return back in pence for consistency
    return pppmInPounds * 100;
  },
  formattedPricePerMonth(state, getters) {
    return (Math.ceil(getters.pricePerPersonPerMonth) / 100).toFixed(2);
  },
  selectedProducts(state) {
    if (state.accountInformation.guid === "") {
      return state.products.reduce((result, product) => {
        let selectedOption = product.options.find(
          (option) => option.code === product.selected,
        );

        if (selectedOption) {
          result.push({
            ...selectedOption,
            type: product.type,
            enabled: true,
          });
        }

        return result;
      }, []);
    } else {
      // Joining tenant
      return state.products.map((product) => ({
        ...product,
        enabled: true,
      }));
    }
  },
  selectedUnselectedProducts(state) {
    return state.products.reduce((result, product) => {
      const productCode = state.pickedProductPriceCodes[product.type];
      const UnselectedProductCode =
        state.unpickedProductPriceCodes[product.type] || null;

      let selectedOption = product.options.find((option) => {
        return (
          option.code === productCode || option.code === UnselectedProductCode
        );
      });

      selectedOption.type = product.type;
      result.push(selectedOption);

      return result;
    }, []);
  },
  selectedProductPriceIds(state, getters) {
    return (
      getters.selectedProducts
        // .filter((product) => !product.code.includes("_NONE"))
        .map((product) => product.priceId)
    );
  },
  hasAddress(state) {
    const houseAddress = state.houseInformation.address;
    return (
      houseAddress.hasOwnProperty("guid") ||
      houseAddress.hasOwnProperty("fullAddress")
    );
  },
  hasAddressGuid(state) {
    const houseAddress = state.houseInformation.address;
    return houseAddress.hasOwnProperty("guid");
  },
  addressGuid(state) {
    const houseAddress = state.houseInformation.address;

    if (houseAddress.hasOwnProperty("guid")) {
      return houseAddress.guid;
    }

    return false;
  },
  fullAddress(state) {
    const houseAddress = state.houseInformation.address;

    if (houseAddress.hasOwnProperty("fullAddress")) {
      return houseAddress.fullAddress;
    }

    return false;
  },
  addressLine1(state) {
    const houseAddress = state.houseInformation.address;

    if (houseAddress.hasOwnProperty("fullAddress")) {
      return houseAddress.fullAddress.line1;
    } else if (houseAddress.hasOwnProperty("guid")) {
      return houseAddress.line1;
    }

    return null;
  },
  addressLine1And2(state) {
    const houseAddress = state.houseInformation.address;

    let address = [];
    if (houseAddress.line1) {
      address.push(houseAddress.line1);
    }

    if (houseAddress.line2) {
      address.push(houseAddress.line2);
    }

    return address.join(", ");
  },
  getCustomerAddressString(state) {
    const houseAddress = state.houseInformation.address;
    if (houseAddress.hasOwnProperty("fullAddress")) {
      // loop through full address and create string from filled values
      return Object.keys(houseAddress.fullAddress)
        .reduce((acc, cur) => {
          if (houseAddress.fullAddress[cur])
            acc.push(houseAddress.fullAddress[cur]);
          return acc;
        }, [])
        .join(", ");
    } else if (houseAddress.hasOwnProperty("guid")) {
      let fullAddress = [];

      if (houseAddress.organisationName) {
        fullAddress.push(houseAddress.organisationName);
      }

      if (houseAddress.line1) {
        fullAddress.push(houseAddress.line1);
      }

      if (houseAddress.line2) {
        fullAddress.push(houseAddress.line2);
      }

      return fullAddress.join(", ");
    } else {
      return "";
    }
  },
  hasBroadband(state, getters) {
    const products =
      getters.selectedProducts.length > 0
        ? getters.selectedProducts
        : getters.accountInformation.products;

    return (
      products.filter(
        (product) =>
          product.type.toLowerCase() === "broadband" &&
          !product.code.includes("_NONE"),
      ).length > 0
    );
  },
  isLeadTenant(state, getters) {
    return !!getters.postcode;
  },
  tenancyStartDate(state) {
    let startDate = "";

    if (state.houseInformation.startDate) {
      startDate = state.houseInformation.startDate;
    } else if (state.accountInformation.tenancyStartDate) {
      startDate = state.accountInformation.tenancyStartDate;
    }

    return startDate;
  },
  tenancyEndDate(state) {
    let endDate = "";

    if (state.houseInformation.endDate) {
      endDate = state.houseInformation.endDate;
    } else if (state.accountInformation.tenancyEndDate) {
      endDate = state.accountInformation.tenancyEndDate;
    }

    return endDate;
  },
  guid(state) {
    return state.accountInformation.guid;
  },
  hasValidDiscount(state) {
    return state.discount.isValid;
  },
  discountedProduct(state, getters) {
    if (!getters.hasValidDiscount) {
      return null;
    }

    return state.products.find(
      (product) => product.type === state.discount.productType,
    );
  },
  hasUsedDiscount(state, getters) {
    const discountedProduct = getters.discountedProduct;
    if (!discountedProduct) {
      return false;
    }

    return discountedProduct.selected === state.discount.productCode;
  },
  discountedProductOption(state, getters) {
    const discountedProduct = getters.discountedProduct;
    if (!discountedProduct) {
      return false;
    }

    return discountedProduct.options.find(
      (option) => option.code === state.discount.productCode,
    );
  },
  accountInformation(state) {
    return state.accountInformation;
  },
  getValidationErrors: (state) => state.validationErrors,
  shouldShowVouchers(state) {
    if (!state.featureFlags.hasOwnProperty("allow_vouchers")) {
      return false;
    }

    return !!state.featureFlags.allow_vouchers.value;
  },
  getDefaultVoucher(state) {
    if (!state.featureFlags.hasOwnProperty("default_voucher")) {
      return false;
    }

    return state.featureFlags.default_voucher.value;
  },
  getAutoApplyDefaultVoucher(state) {
    if (
      !state.featureFlags.hasOwnProperty("auto_apply_default_voucher_codes")
    ) {
      return false;
    }

    return state.featureFlags["auto_apply_default_voucher_codes"].value;
  },
  getEditFlowTest(state) {
    if (!state.featureFlags.hasOwnProperty("edit_flow_test")) {
      return false;
    }

    return state.featureFlags["edit_flow_test"].value;
  },
  getShowMonthlyPriceInfo(state) {
    if (!state.featureFlags.hasOwnProperty("show_monthly_price_info")) {
      return false;
    }

    return !!state.featureFlags["show_monthly_price_info"].value;
  },
  shouldShowWhatsAppConsent(state) {
    if (!state.featureFlags.hasOwnProperty("show_whatsapp_consent")) {
      return false;
    }

    return !!state.featureFlags["show_whatsapp_consent"].value;
  },
  isTenancyStartDateDynamic(state) {
    if (!state.featureFlags.hasOwnProperty("dynamic_tenancy_start_date")) {
      return false;
    }

    return !!state.featureFlags.dynamic_tenancy_start_date.value;
  },
  getTenancyLengthAlert(state) {
    if (!state.featureFlags.hasOwnProperty("tenancy_length_alert")) {
      return false;
    }

    return !!state.featureFlags["tenancy_length_alert"].value;
  },
};
